import React from "react";
import dayjs from "dayjs";
import { CircleCheck, CircleX, Clock, FileText, LoaderCircle } from "lucide-react";

/**
 * How one report reads in the upload list: its status badge, its file name cell
 * and the date and size formatting around them.
 */

// queued → uploading → processing → success | failed
export const ACTIVE_STATUSES = new Set(["queued", "uploading", "processing"]);

const STATUS_META = {
  queued: { label: "Queued", tone: "bg-slate-100 text-slate-600", Icon: Clock },
  uploading: {
    label: "Uploading",
    tone: "bg-blue-50 text-blue-700",
    Icon: LoaderCircle,
    spin: true,
  },
  processing: {
    label: "Processing",
    tone: "bg-amber-50 text-amber-700",
    Icon: LoaderCircle,
    spin: true,
  },
  success: {
    label: "Successful",
    tone: "bg-emerald-50 text-emerald-700",
    Icon: CircleCheck,
  },
  failed: { label: "Failed", tone: "bg-red-50 text-red-700", Icon: CircleX },
};

export const formatDate = (value) =>
  value && dayjs(value).isValid() ? dayjs(value).format("MMM D, YYYY · h:mm A") : "—";

export const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.queued;
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${meta.tone}`}
    >
      <Icon
        className={`h-3.5 w-3.5 ${meta.spin ? "animate-spin" : ""}`}
        aria-hidden="true"
      />
      {meta.label}
    </span>
  );
};

/** The file name cell: PDF icon, name on one line, size underneath. */
export const FileNameCell = ({ fileName, size, muted = false }) => (
  <div className="flex items-start gap-3">
    <FileText
      className={`mt-0.5 h-5 w-5 shrink-0 ${muted ? "text-slate-300" : "text-red-500"}`}
      aria-hidden="true"
    />
    <div className="min-w-0">
      <p
        className={`max-w-[260px] truncate font-medium ${
          muted ? "text-slate-500" : "text-slate-800"
        }`}
        title={fileName}
      >
        {fileName}
      </p>
      {size ? <p className="text-xs text-slate-400">{formatSize(size)}</p> : null}
    </div>
  </div>
);
